/**
 * What the Observers step's read-only *Suggestions* layer shows for one camera (spec
 * `observer-autogen-review-ui-preview`, R4–R6, R10): the outlines to draw, their labels, and the
 * camera's status in plain words. Pure — the host owns the read.
 */
import type {
  HeatGridPart,
  HeatGridWindow,
  ObserverProposal,
  ObserverProposalWarning,
} from '@repo/frontend-types';
import type { ObserverProposalsOutcome } from '@repo/front-end-services';

/** One sentence per engine warning. A test pins the key set to the middleware's list. */
export const WARNING_TEXT: Record<ObserverProposalWarning, string> = {
  'plan-has-no-linked-cameras': 'No camera on the saved plan is linked to a stream',
  'camera-not-in-plan': 'This camera isn’t on the saved plan yet',
  'camera-cap-exceeded': 'Too many cameras were requested',
  'camera-endpoint-mismatch': 'The plan links this camera to a different stream',
  'camera-not-found': 'The stream isn’t known',
  'frame-size-unknown': 'The camera’s picture size isn’t known yet',
  'frame-size-mismatch': 'Points fell outside the picture size the heat was recorded at',
  'no-heat-in-window': 'No activity was recorded in this window',
  'window-incomplete': 'Some days in this window aren’t binned yet',
  'grid-read-failed': 'The heat history couldn’t be read',
  'proposal-failed': 'The suggestion engine failed on this camera',
  'uncalibrated-ordering': 'Not calibrated — calibrate this camera to get named suggestions',
  'calibration-out-of-date':
    'The calibration is out of date — recalibrate this camera to get named suggestions',
  'request-budget-exhausted': 'Ran out of time — try again',
  'drawn-observer-unscored': 'An observer drawn on this camera has too few points to compare',
};

export const PART_LABEL: Record<HeatGridPart, string> = {
  FEET: 'Feet',
  HIPS: 'Hips',
  TORSO: 'Torso',
  LEGS: 'Legs',
  HEAD: 'Head',
};

const WINDOW_DAYS: Record<HeatGridWindow, number> = { '1d': 1, '7d': 7, '14d': 14, '28d': 28 };

/** A suggestion as the drawing area needs it: picture-pixel points and a one-line label. */
export interface SuggestionOutline {
  id: string;
  points: { x: number; y: number }[];
  label: string;
}

/** "Treadmill 3 · Feet · 82%" — the machine, the part the ENGINE chose, and its confidence (R5). */
export const suggestionLabel = (p: ObserverProposal): string => {
  const name = p.targetName ?? 'Unlinked';
  const part = PART_LABEL[p.observer.trackSide] ?? p.observer.trackSide;
  const pct = Math.round(Math.max(0, Math.min(1, p.confidence)) * 100);
  return `${name} · ${part} · ${pct}%`;
};

/** The read's state as the host holds it: not asked yet, in flight, or settled. */
export type SuggestionReadState = 'idle' | 'loading' | ObserverProposalsOutcome;

export interface SuggestionView {
  outlines: SuggestionOutline[];
  /** The status line (a polite live region). Empty while idle. */
  statusText: string;
  /** Extra sentences shown under the status line. */
  warnings: string[];
  /** The *Suggestions* switch's description line. */
  description: string;
  /** Which action the status line offers: Retry after a failure, Refresh after an answer. */
  action: 'retry' | 'refresh' | null;
}

const EMPTY: SuggestionView = {
  outlines: [],
  statusText: '',
  warnings: [],
  description: 'Off',
  action: null,
};

/**
 * The view for the selected camera. A failed read is never "no suggestions" (Failure States):
 * only a READY answer whose camera ran (`status: ok`) with zero proposals says so.
 */
export const suggestionView = (
  state: SuggestionReadState,
  rtspBid: string,
  window: HeatGridWindow,
): SuggestionView => {
  if (state === 'idle') return EMPTY;
  if (state === 'loading') {
    return {
      outlines: [],
      statusText: 'Finding suggestions…',
      warnings: [],
      description: 'Loading…',
      action: null,
    };
  }
  if (state.status === 'cancelled') return EMPTY;
  if (state.status === 'failed') {
    return {
      outlines: [],
      statusText: `Couldn’t load suggestions — ${state.reason}`,
      warnings: [],
      description: 'Couldn’t load',
      action: 'retry',
    };
  }
  const answer = state.value;
  const camera = answer.cameras.find(c => c.rtspEndpointBid === rtspBid);
  if (!camera) {
    // Absent, not errored: the saved plan does not hold this camera (yet).
    const text = answer.camerasNotInPlan.includes(rtspBid)
      ? WARNING_TEXT['camera-not-in-plan']
      : (answer.warnings.map(w => WARNING_TEXT[w]).find(Boolean) ??
        WARNING_TEXT['camera-not-in-plan']);
    return {
      outlines: [],
      statusText: text,
      warnings: [],
      description: 'Not on the saved plan',
      action: 'refresh',
    };
  }
  const warnings = camera.warnings.map(w => WARNING_TEXT[w] ?? w);
  if (camera.detail && camera.status !== 'ok') warnings.push(camera.detail);
  const days = WINDOW_DAYS[window];
  if (camera.status !== 'ok' || !camera.result) {
    return {
      outlines: [],
      statusText:
        camera.status === 'failed'
          ? 'Suggestions failed for this camera'
          : 'No suggestions for this camera',
      warnings,
      description: camera.status === 'failed' ? 'Failed' : 'Skipped',
      action: 'refresh',
    };
  }
  const proposals = camera.result.proposals;
  const outlines = proposals
    .filter(p => p.observer.points.length >= 3)
    .map(p => ({ id: p.observer.refid, points: p.observer.points, label: suggestionLabel(p) }));
  return {
    outlines,
    statusText:
      outlines.length === 0
        ? `No suggestions for this camera in the last ${days} day${days === 1 ? '' : 's'} · from the saved plan`
        : `${outlines.length} suggested from the last ${days} day${days === 1 ? '' : 's'} · from the saved plan`,
    warnings,
    description: `${days} day${days === 1 ? '' : 's'} · ${outlines.length} suggested`,
    action: 'refresh',
  };
};

/** The suggestions' text alternative for the picture's description (R7). */
export const suggestionsDescription = (outlines: SuggestionOutline[]): string =>
  outlines.length === 0 ? '' : `Suggested: ${outlines.map(o => o.label).join('; ')}.`;
